import React, { useCallback, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { IntroducingBroker } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import { InputFilterCell } from 'components/Filter/FilterCell'
import { Link } from 'components/Link'
import { Loading } from 'components/Loading'
import { normalizeFilter } from 'features/DataGrid/helpers'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import stringComparator from 'utils/stringComparator'

import { IntroducingBrokerProfileProps } from './types'

export const IntroducingBrokerProfileIbsPage: React.FC<IntroducingBrokerProfileProps> = (props) => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const { profile } = props
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )

  const callback = useCallback(
    () => partnershipsApiClient.getIntroducingBrokerProfileIBs(profile.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile.id]
  )
  const { data, isLoading } = useFetchOne(callback)

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data && data.length >= 1) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState(getPaginationInitialState())
  }

  const gridData = filterBy(data || [], filter).sort((a: IntroducingBroker, b: IntroducingBroker) =>
    stringComparator(a.name, b.name)
  )

  return (
    <Loading isLoading={isLoading}>
      <Grid
        resizable
        pageable
        filterable
        data={gridData.slice(paginationState.skip, paginationState.take + paginationState.skip)}
        filter={filter}
        skip={paginationState.skip}
        take={paginationState.take}
        total={gridData.length}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        data-test='table'
      >
        <Column
          field='name'
          title='Name'
          filterCell={InputFilterCell}
          cell={(cellProps: GridCellProps) => (
            <td style={{ color: 'var(--link)', textDecoration: 'underline', width: 'auto' }}>
              <Link
                to={{
                  pathname: generatePath(links.recordsIntroducingBrokers.detailsUrl as string, {
                    id: cellProps.dataItem.id,
                    slug: 'details',
                  }),
                }}
              >
                {cellProps.dataItem.name}
              </Link>
            </td>
          )}
        />
        <Column
          title='Updated At'
          filterable={false}
          cell={(gridCellProps: GridCellProps) => (
            <td>{formatLocalDateUTC(gridCellProps.dataItem.updatedDate, true)}</td>
          )}
        />
      </Grid>
    </Loading>
  )
}
